/**
 * Keyboard focus helpers for a drawer whose focus trap can steal a Tab.
 *
 * Radix's FocusScope (inside vaul's Drawer.Content) watches its subtree, and
 * when a node is removed while `document.activeElement` is <body> it treats
 * focus as lost and focuses its own container. A native Tab passes through
 * exactly that state: the field blurs, focus is on <body> until the next field
 * takes it, and any widget that tidies its DOM on blur trips the observer in
 * between. Two do on the checkout — Mapbox clears its aria-live description,
 * and react-select removes its menu and live-region text — and in both cases
 * the donor lands on the drawer instead of the next field (A11-003, EMB-012).
 */

/** Everything the browser's own Tab order would visit. */
const TABBABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([type="hidden"]):not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]',
].join(', ');

/**
 * Rendered, as the browser's Tab order sees it. A control inside a
 * `display: none` or `visibility: hidden` ancestor is skipped by Tab, so it
 * must be skipped here too — checking only the control's own style counted,
 * for example, a checkbox in a summary hidden below a breakpoint.
 *
 * Opacity is deliberately NOT a test: an `opacity: 0` control is still in the
 * native Tab order, and these helpers exist to reproduce that order.
 *
 * `checkVisibility()` is the real check where a layout engine provides it. The
 * ancestor walk is the fallback for one without (jsdom), and reads the same
 * two properties. A mocked or partial computed style counts as visible.
 */
export function isRenderedForTab(element) {
  if (typeof element.checkVisibility === 'function') {
    return element.checkVisibility({ checkVisibilityCSS: true });
  }
  for (let node = element; node; node = node.parentElement) {
    if (getComputedStyle(node)?.display === 'none') return false;
  }
  return getComputedStyle(element)?.visibility !== 'hidden';
}

function isTabbable(element) {
  if (element.tabIndex < 0) return false;
  if (element.closest('[hidden], [inert], mapbox-search-listbox')) return false;
  return isRenderedForTab(element);
}

/** Every element inside `root` that Tab can land on, in DOM order. */
export function tabbablesIn(root) {
  if (!root) return [];
  return Array.from(root.querySelectorAll(TABBABLE_SELECTOR)).filter(isTabbable);
}

/**
 * The next (or previous) tabbable element after `from`, in DOM order, inside
 * `scope`. `from` itself need not be tabbable any more — a field can be halfway
 * through unmounting — but it must still be in the document.
 *
 * @param {Element} from
 * @param {{ backwards?: boolean, scope?: ParentNode }} [options]
 * @returns {HTMLElement | null}
 */
export function findAdjacentTabbable(from, { backwards = false, scope } = {}) {
  if (!from?.isConnected) return null;
  const root = scope || from.closest('[role="dialog"]') || document;
  const candidates = Array.from(root.querySelectorAll(TABBABLE_SELECTOR)).filter(
    (element) => element === from || isTabbable(element),
  );
  let index = candidates.indexOf(from);
  if (index < 0) {
    // `from` is not itself tabbable (e.g. a wrapper): place it by DOM position.
    index = candidates.findIndex(
      (element) => from.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING,
    );
    if (index < 0) index = candidates.length;
    index -= backwards ? 0 : 1;
  }
  return candidates[index + (backwards ? -1 : 1)] || null;
}
